import React from "react"

import ResizablePanel from "./ResizablePanel"

interface CollapsiblePanelProps {
  children: React.ReactNode
  isVisible: boolean
  onToggle: () => void
  title: string
  width: string
  position?: "left" | "right"
  resizable?: boolean
  verticalDrag?: boolean
  onResize?: (dimensions: { width?: string; height?: string }) => void
  onVerticalResize?: (dimensions: { height?: string }) => void
  className?: string
  collapsed?: React.ReactNode
}

const CollapsiblePanel: React.FC<CollapsiblePanelProps> = ({
  children,
  isVisible = false,
  onToggle = () => {},
  title = "",
  width = "240px",
  position = "left",
  resizable = true,
  verticalDrag = false,
  onResize,
  onVerticalResize,
  className = "",
  collapsed,
}) => {
  // Default collapsed view if not provided
  const defaultCollapsed = (
    <div
      className={`w-10 bg-gray-800 ${
        position === "left" ? "border-r" : "border-l"
      } border-gray-700 flex flex-col items-center cursor-pointer ${
        position === "right" ? "ml-auto" : ""
      }`}
      onClick={(e) => {
        if (e && onToggle) onToggle()
      }}
      title={`Show ${title}`}
    >
      <div className="h-full flex items-center justify-center">
        <div className="rotate-90 whitespace-nowrap text-xs font-medium text-gray-400">
          {title || "Panel"}
        </div>
      </div>
      <div className="py-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={
              position === "left"
                ? "M9 5l7 7-7 7" // Arrow pointing right
                : "M15 19l-7-7 7-7" // Arrow pointing left
            }
          />
        </svg>
      </div>
    </div>
  )

  // Vertical drag handle for individual panel
  const verticalDragHandle = verticalDrag && onVerticalResize && (
    <div
      className="absolute top-0 left-0 right-0 h-2 bg-gray-600 opacity-0 hover:opacity-40 cursor-ns-resize z-30"
      onMouseDown={(e) => {
        e.preventDefault()
        e.stopPropagation()

        const panelElement = e.currentTarget.parentElement
        if (!panelElement) return

        const startY = e.clientY
        const rect = panelElement.getBoundingClientRect()
        const parentRect =
          panelElement.parentElement?.parentElement?.getBoundingClientRect()
        if (!parentRect) return

        const mainContainer = document.querySelector(
          ".flex-1.flex.flex-col.overflow-hidden",
        )
        if (!mainContainer) return
        const mainRect = mainContainer.getBoundingClientRect()

        const handleMouseMove = (moveEvent: MouseEvent) => {
          const currentY = moveEvent.clientY
          const deltaY = startY - currentY

          // Calculate the percentage of the parent container that this panel should occupy
          const totalHeight = mainRect.height
          const newHeight = Math.min(totalHeight * 0.9, rect.height + deltaY)
          const heightPercent = (newHeight / totalHeight) * 100

          // Limit the height to reasonable values
          const clampedPercent = Math.max(15, Math.min(85, heightPercent))

          if (onVerticalResize) {
            onVerticalResize({ height: `${clampedPercent}%` })
          }
        }

        const handleMouseUp = () => {
          document.removeEventListener("mousemove", handleMouseMove)
          document.removeEventListener("mouseup", handleMouseUp)
        }

        document.addEventListener("mousemove", handleMouseMove)
        document.addEventListener("mouseup", handleMouseUp)
      }}
    />
  )

  // Get custom panel classes
  const panelClasses = `relative bg-gray-800 ${
    position === "left" ? "border-r" : "border-l"
  } border-gray-700 ${className}`

  // Expanded content
  const contentElement = resizable ? (
    <ResizablePanel
      className={panelClasses}
      style={{ width: width || "240px" }}
      resizeHandlePosition="right"
      resizeHandleClassName={`absolute h-full w-2 cursor-ew-resize ${
        position === "left" ? "right-0" : "left-0"
      } top-0 z-10 bg-gray-700 opacity-0 hover:opacity-50`}
      onResize={onResize}
    >
      {verticalDragHandle}
      {children}
    </ResizablePanel>
  ) : (
    <div className={panelClasses} style={{ width: width || "240px" }}>
      {verticalDragHandle}
      {children}
    </div>
  )

  return isVisible ? contentElement : collapsed || defaultCollapsed
}

export default CollapsiblePanel
